/**
 * Two billing-related modals that sit alongside the briefing flow:
 *
 *   · ConfirmBriefingModal — pre-flight confirmation before debiting
 *     a credit. Shown on BriefingProgress mount when force=1 or when
 *     no same-day cache exists. Prevents accidental ~$0.32 generations.
 *
 *   · OutOfCreditsModal — caught when /chat/v2/start returns 402.
 *     Shows the gap (balance/needed) and routes the user to /billing/packs.
 *
 * Both render a backdrop + centred card. They use the existing card
 * tokens — no new CSS. Closing the modal returns the user to the
 * previous screen (BriefingProgress treats dismissal as cancel).
 */
import { useEffect, useRef, useState } from "react";
import { IconSparkle, IconArrowRight } from "@/components/icons";
import {
  createAiSingleOrder, gateKind, gateMessage, getAiBudget, getAiSingles, startAiTopup,
  topupOptionsFor, type AiSingle, type BriefingTier,
} from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useFocusTrap } from "@/hooks/useFocusTrap";

/** The two equity-briefing tiers the IFA chooses between. Price is shown in ₹
 *  AND credits (1 credit = ₹100); the backend debits the credit cost. Product
 *  names only — the underlying engine is intentionally not surfaced. */
interface TierOption {
  tier: BriefingTier;
  name: string;
  tagline: string;
  priceInr: number;
  costCredits: number;
  blurb: string;
}

export const TIER_OPTIONS: TierOption[] = [
  {
    tier: "executive",
    name: "Executive Summary",
    tagline: "Fast · decision-ready",
    priceInr: 200,
    costCredits: 2,
    blurb:
      "A concise, client-ready briefing on the same underlying data — leaner narrative, quicker turnaround.",
  },
  {
    tier: "institutional",
    name: "Institutional",
    tagline: "Full depth",
    priceInr: 500,
    costCredits: 5,
    blurb:
      "The flagship briefing — full DuPont/scenario depth and the richest prose. For your highest-stakes recommendations.",
  },
  {
    tier: "research",
    name: "Deep Dive",
    tagline: "Research-grade · deepest",
    priceInr: 2000,
    costCredits: 20,
    blurb:
      "Our most thorough report — the deepest reasoning across multi-year financials, peers and macro, with full valuation-sensitivity, thesis-invalidation and catalyst analysis. For a single, high-conviction name.",
  },
];

interface ConfirmBriefingModalProps {
  ticker: string;
  credits: number | null;
  /** Force-refresh path (cache exists but user wants fresh) shows a
   *  different headline since the user is explicitly re-running. */
  isForceRefresh?: boolean;
  /** Whether the backend actually debits + 402s on insufficient balance.
   *  When false (BILLING_ENFORCED off), the modal must NOT gate the Run
   *  buttons on balance — the server starts the job regardless, so a disabled
   *  button just strands the user. Defaults to true (safe gate). */
  billingEnforced?: boolean;
  onCancel: () => void;
  onConfirm: (tier: BriefingTier) => void;
}

export function ConfirmBriefingModal({
  ticker,
  credits,
  isForceRefresh,
  billingEnforced = true,
  onCancel,
  onConfirm,
}: ConfirmBriefingModalProps) {
  useEscapeKey(onCancel);

  return (
    <ModalBackdrop onClose={onCancel}>
      <div className="card" style={{ padding: "28px 30px", maxWidth: 520, width: "100%" }}>
        <div className="kicker">
          <span className="sq" />
          {isForceRefresh ? "Re-run briefing" : "Choose a briefing tier"}
        </div>
        <h2
          style={{
            fontFamily: "var(--editorial)",
            fontWeight: 500,
            fontSize: 26,
            letterSpacing: "-0.01em",
            marginTop: 10,
            lineHeight: 1.2,
          }}
        >
          {isForceRefresh ? "Re-run " : "Briefing for "}
          <b>{ticker}</b>
        </h2>
        <p className="muted" style={{ fontSize: 13.5, marginTop: 10, lineHeight: 1.55 }}>
          Pick a tier — takes ~20 minutes. Same-day re-reads of the result are
          free afterwards.
          {billingEnforced && (
            <>
              {" "}
              Your balance:{" "}
              <b style={{ fontFamily: "var(--mono)" }}>{credits ?? "—"} credits</b>.
            </>
          )}
        </p>

        <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
          {TIER_OPTIONS.map((opt) => {
            const tooFew =
              billingEnforced && credits != null && credits < opt.costCredits;
            return (
              <div
                key={opt.tier}
                className="row between"
                style={{
                  alignItems: "stretch",
                  gap: 14,
                  padding: "14px 16px",
                  background: "var(--paper-2)",
                  border: "1px solid var(--line-soft)",
                  borderRadius: 12,
                  opacity: tooFew ? 0.6 : 1,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div className="row gap-2" style={{ alignItems: "baseline" }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{opt.name}</span>
                    <span className="muted" style={{ fontSize: 11, letterSpacing: "0.02em" }}>
                      {opt.tagline}
                    </span>
                  </div>
                  <div
                    className="muted"
                    style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.5 }}
                  >
                    {opt.blurb}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    <b>₹{opt.priceInr}</b>{" "}
                    <span className="muted" style={{ fontSize: 12 }}>
                      · {opt.costCredits} credits
                    </span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => onConfirm(opt.tier)}
                    disabled={tooFew}
                    title={tooFew ? "Not enough credits for this tier" : undefined}
                  >
                    <IconSparkle /> Run
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {!billingEnforced && (
          // Billing not yet enforced — be honest that runs are free in preview.
          <div
            className="muted"
            style={{ marginTop: 14, fontSize: 12.5, lineHeight: 1.5 }}
          >
            Billing isn’t enabled yet — runs are <b>free during preview</b>; no
            credits are debited.
          </div>
        )}

        <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

interface OutOfCreditsModalProps {
  ticker?: string;
  balance: number;
  needed: number;
  onCancel: () => void;
  onBuyMore: () => void;
}

export function OutOfCreditsModal({
  ticker,
  balance,
  needed,
  onCancel,
  onBuyMore,
}: OutOfCreditsModalProps) {
  useEscapeKey(onCancel);
  return (
    <ModalBackdrop onClose={onCancel}>
      <div className="card" style={{ padding: "28px 30px", maxWidth: 460, width: "100%" }}>
        <div className="kicker">
          <span className="sq" />
          Out of credits
        </div>
        <h2
          style={{
            fontFamily: "var(--editorial)",
            fontWeight: 500,
            fontSize: 26,
            letterSpacing: "-0.01em",
            marginTop: 10,
            lineHeight: 1.2,
          }}
        >
          You need {needed > 1 ? `${needed} credits` : "1 credit"} to run{" "}
          {ticker ? <b>{ticker}</b> : "this briefing"}.
        </h2>
        <p
          className="muted"
          style={{ fontSize: 13.5, marginTop: 10, lineHeight: 1.55 }}
        >
          Current balance: <b>{balance}</b>. Buy a pack to continue — credits
          never expire, and same-day re-reads of any briefing stay free.
        </p>

        <div
          className="row gap-2"
          style={{ marginTop: 22, justifyContent: "flex-end" }}
        >
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={onBuyMore}>
            <IconSparkle /> Buy credits <IconArrowRight />
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ── shared scaffolding ────────────────────────────────────────────────

function ModalBackdrop({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  // F11: every credit modal renders through this one scaffold, so trapping
  // focus here covers all of them. Before this, `aria-modal="true"` below was
  // telling assistive tech the rest of the page was inert while Tab walked
  // straight out of the dialog into it.
  const dlgRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dlgRef, true);
  return (
    <div
      ref={dlgRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        // Click backdrop = close. Card uses stopPropagation to avoid this.
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20, 26, 32, 0.55)",
        display: "grid",
        placeItems: "center",
        zIndex: 1000,
        padding: 24,
      }}
    >
      <div onMouseDown={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480 }}>
        {children}
      </div>
    </div>
  );
}

function useEscapeKey(onEscape: () => void) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onEscape]);
}

/** Inline 402 notice with the door that actually opens: the subscription gate
 *  offers Subscribe (or Renew) and, when the server says a bought pack also
 *  unlocks the SKU, the credit packs beside it; a plain credits 402 offers the
 *  packs alone.
 *
 *  Why: a free-tier retail user pressing Deep Dive used to read "StockSense Pro
 *  is required for this. Subscribe to continue." as plain text — no button, and
 *  no mention that a ₹99 pack unlocks it too — while the fallback copy in the
 *  same component said "You've used this month's AI credits", which was false
 *  for an account that had never had a monthly grant. Advisers never hit the
 *  subscription gate, so `role` decides which packs are priced. */
export function GateActions({ err, fallback, role, onSubscribe, feature, onPaid, prefillEmail, prefillName }: {
  /** The 402 (or null when the caller knows the balance is short before asking). */
  err: unknown;
  fallback: string;
  role?: string;
  onSubscribe: () => void;
  /** ai_budget CREDIT_COSTS key of the button that was pressed. When the server
   *  prices it as a single buy, the primary button is "Buy this <label> · ₹N". */
  feature?: string;
  /** Called once the credits have landed in the wallet — re-run the action. */
  onPaid?: () => void;
  prefillEmail?: string;
  prefillName?: string;
}) {
  const { kind, canBuyCredits } = gateKind(err);
  // No error object means the caller gated on the balance up front (e.g. the
  // Health Report card at 0 credits): that is a credits wall, and packs fix it.
  const effectiveKind = err == null && feature ? "credits" : kind;
  const text = gateMessage(err, fallback);
  const subGate = effectiveKind === "subscribe" || effectiveKind === "renew";
  const creditsOk = canBuyCredits || effectiveKind === "credits";
  const packs = creditsOk ? topupOptionsFor(role) : [];
  const single = useAiSingle(creditsOk ? feature : undefined);
  const [buyState, setBuyState] = useState<"idle" | "checkout" | "confirming" | "failed">("idle");
  const btn: React.CSSProperties = { padding: "6px 10px", fontSize: 12 };

  const buySingle = async () => {
    if (!single || buyState !== "idle") return;
    setBuyState("checkout");
    try {
      const before = await getAiBudget().then((b) => b.topup_credits).catch(() => null);
      const order = await createAiSingleOrder(single.feature);
      await openRazorpayCheckout({
        keyId: order.key_id, orderId: order.order_id, amountPaise: order.amount,
        packName: `${order.single.label} · ${order.single.credits} AI credits`,
        prefillEmail, prefillName,
        onDismiss: () => setBuyState("idle"),
        onSuccess: () => {
          // Checkout's success callback is NOT the grant — the webhook is. Wait
          // for the wallet to show the credits, then run what the user asked for.
          setBuyState("confirming");
          void waitForCredits(before, order.single.credits).then((landed) => {
            if (landed) { setBuyState("idle"); onPaid?.(); } else setBuyState("failed");
          });
        },
      });
    } catch {
      setBuyState("failed");
    }
  };

  const body = buyState === "confirming"
    ? "Payment received — adding your credits…"
    : buyState === "failed"
      ? "Payment is being confirmed. If your credits do not appear in a minute, they will be added automatically and you can run this again."
      : text;

  return (
    <div data-testid="gate-actions" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div>{body}</div>
      {buyState !== "confirming" && (subGate || single || packs.length > 0) && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {single && (
            <button type="button" className="a-btn a-btn-primary" style={btn} disabled={buyState === "checkout"}
              onClick={() => { void buySingle(); }}>
              {buyState === "checkout" ? "Opening payment…"
                : /^\d/.test(single.label) ? `Buy ${single.label} · ₹${single.price_inr}`
                : `Buy this ${single.label} · ₹${single.price_inr}`}
              {buyState !== "checkout" && (
                // "used now" is true for a single action (Deep Dive: 3 credits, 3
                // spent); a counted label ("3 questions") spends one per use.
                <span style={{ fontWeight: 400, opacity: 0.85 }}>
                  {/^\d/.test(single.label) ? ` (${single.credits} credits)` : ` (${single.credits} credits, used now)`}
                </span>
              )}
            </button>
          )}
          {subGate && (
            <button type="button" className={`a-btn ${single ? "" : "a-btn-primary"}`.trim()} style={btn} onClick={onSubscribe}>
              {effectiveKind === "renew" ? "Renew StockSense Pro" : "Subscribe · ₹199/mo"}
            </button>
          )}
          {packs.length > 0 && (
            /* One group, so the three packs wrap together rather than one pack
               trailing the Subscribe button and two dropping to the next row. */
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 11.5, opacity: 0.75 }}>
                {single || subGate ? "or buy a credit pack, cheaper per credit:" : "Buy AI credits:"}
              </span>
              {packs.map(([sku, label]) => (
                <button key={sku} type="button" className={`a-btn ${single || subGate ? "" : "a-btn-primary"}`.trim()} style={btn}
                  onClick={() => { void startAiTopup(sku); }}>{label}</button>
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** Server price list for single-action buys, fetched once per page and shared. */
let _singlesPromise: Promise<AiSingle[]> | null = null;
function loadAiSingles(): Promise<AiSingle[]> {
  if (!_singlesPromise) {
    _singlesPromise = getAiSingles().then((r) => r.singles ?? []).catch(() => {
      _singlesPromise = null;   // let the next render retry after a blip
      return [];
    });
  }
  return _singlesPromise;
}
/** Test seam: forget the cached price list. */
export function __resetAiSinglesCache() { _singlesPromise = null; }

function useAiSingle(feature: string | undefined): AiSingle | null {
  const [single, setSingle] = useState<AiSingle | null>(null);
  useEffect(() => {
    if (!feature) { setSingle(null); return; }
    let off = false;
    void loadAiSingles().then((list) => {
      if (!off) setSingle(list.find((s) => s.feature === feature) ?? null);
    });
    return () => { off = true; };
  }, [feature]);
  return single;
}

/** Poll the wallet until `topup_credits` has grown by at least `credits` over
 *  `before` (or simply grown, when the starting balance was unreadable). The
 *  webhook usually lands within a second or two; give it ~20s. */
async function waitForCredits(before: number | null, credits: number): Promise<boolean> {
  for (let i = 0; i < 10; i++) {
    try {
      const b = await getAiBudget();
      if (before == null ? b.topup_credits > 0 : b.topup_credits >= before + credits) return true;
    } catch { /* transient; keep polling */ }
    await new Promise((r) => setTimeout(r, 2000));
  }
  return false;
}
